import { ComponentType, ComponentProps } from 'react';
import { themeColors, daimlerFontFamily } from './design-colors';
import { styled } from '@material-ui/core/styles';
import { defaultTypography } from '@backstage/theme';

// Common typography configuration
export const commonTypography = {
  ...defaultTypography,
  htmlFontSize: 16,
  fontFamily: daimlerFontFamily.primary,
  h1: {
    ...defaultTypography.h1,
    fontWeight: 700,
    fontSize: '2.5rem',
    lineHeight: 1.2,
  },
  h2: {
    ...defaultTypography.h2,
    fontWeight: 600,
    fontSize: '2rem',
    lineHeight: 1.3,
  },
  h3: {
    ...defaultTypography.h3,
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.4,
  },
  h4: {
    ...defaultTypography.h4,
    fontWeight: 500,
    fontSize: '1.25rem',
    lineHeight: 1.4,
  },
  body1: {
    fontFamily: daimlerFontFamily.primary,
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontFamily: daimlerFontFamily.primary,
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.01071em',
  },
  subtitle1: {
    fontFamily: daimlerFontFamily.primary,
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontFamily: daimlerFontFamily.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },
  caption: {
    fontFamily: daimlerFontFamily.primary,
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontFamily: daimlerFontFamily.primary,
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase',
  },
};

// Common component overrides
export const commonComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0, 103, 127, 0.2)',
        },
        variants: [],
      },
      contained: {
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 103, 127, 0.3)',
          transform: 'translateY(-1px)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        fontWeight: 500,
        fontSize: '0.75rem',
        transition: 'all 0.2s ease-in-out',
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover' as const,
    },
    styleOverrides: {
      root: {
        textDecoration: 'none',
        fontWeight: 500,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },
};

/**
 * Table header colours.
 *
 * Backstage marks the catalog's first column (`Name`) as `highlight`, which
 * makes its Table set `color: palette.textContrast` as an *inline* style on
 * that one header cell. Inline styles beat class rules, so that column can
 * never track the sorted state the way the others do.
 *
 * Styling the sort label itself sidesteps that: a rule on the element wins
 * over a colour merely inherited from its cell, so every header — highlighted
 * or not — is muted at rest and reaches full strength only while it is the
 * active sort. Selectors are one class more specific than the MUI rules they
 * replace so they win regardless of stylesheet injection order.
 */
export const createTableHeaderOverrides = (colors: {
  /** Resting header label — `text.secondary`. */
  idle: string;
  /** Hovered or actively-sorted header label — `text.primary`. */
  active: string;
}) => ({
  BackstageTableHeader: {
    styleOverrides: {
      header: {
        '& .MuiTableSortLabel-root': {
          color: colors.idle,
        },
        '& .MuiTableSortLabel-root:hover': {
          color: colors.active,
        },
        '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
          color: colors.active,
        },
      },
    },
  },
});

// Shared sidebar interaction styles used by both light and dark themes.
export const commonSidebarInteractionStyles = {
  fontStyle: 'normal' as const,
  textDecoration: 'none',
  '&, &:hover, &:focus, &:active, &:visited': {
    fontStyle: 'normal' as const,
    textDecoration: 'none',
  },
  '& a, & a:hover, & a:focus, & a:active, & a:visited': {
    fontStyle: 'normal' as const,
    textDecoration: 'none',
  },
};

export const commonSidebarItemRootStyles = {
  borderRadius: 8,
  margin: '2px 8px',
  transition: 'all 0.2s ease-in-out',
  ...commonSidebarInteractionStyles,
  '& .MuiListItemIcon-root': {
    minWidth: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiListItemIcon-root svg': {
    width: '20px',
    height: '20px',
  },
};

export const commonSidebarSubmenuItemStyles = {
  borderRadius: 8,
  margin: '2px 8px',
  ...commonSidebarInteractionStyles,
};

export const commonSidebarLabelStyles = {
  fontWeight: 500,
  fontStyle: 'normal' as const,
};

export const commonSidebarLinkResetStyles = {
  // Sidebar items are navigational buttons, not text links.
  '[class*="BackstageSidebar"] &': {
    ...commonSidebarInteractionStyles,
  },
};

export const ThemedSidebarDivider = styled('hr')(
  ({ theme }) => ({
    height: 1,
    width: '100%',
    background: themeColors.petrol20,
    border: 'none',
    margin: theme.spacing(1.2, 0),
  }),
  { name: 'BackstageSidebarDivider' },
) as ComponentType<ComponentProps<'hr'>>;
